import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Observable, BehaviorSubject } from 'rxjs';
import { RestAPIService } from 'app/services/rest-api.service';
import { getAllChartTypesForComponent, IFeatures } from 'app/services/graphqlApi.service';
import { getChartsTypesForComponent, getChartsTypesForGroup, getChartsTypesForModel, getChartTypes, getComponentPanelsByIds, getComponentSubChartTypes, getDashboardPanelsByPanelIds, getDashboardsById, getGroupDashboardsById, getGroupPanelsById, getGroupSubChartTypes, getModelSubChartTypes, getRefPanelTypes } from 'app/shared/constants/gql';
import { IAggrPayload, IAssetEvents, IAssetTableDelete, IDashboardPayload, IEventsResponse, IGlobalLineCharts, IPanelPayload, IPanelPayloadResponse, IRangeForm, IRealTimeTags, IRefPanelTypes, ISelectedPanelRange, IWriteBack } from '../Models/chart-settings.model';
import { IAssetTableData, IGroupAssetsTableRow } from 'app/assets/interfaces/interfaces';
import { EComponentTabs, EEntityTypes, ERanges, ETagsTableCellHeights, ETagsTableColAlignments, RestRequestType } from 'app/shared/enums';
import { IDropdown } from 'app/shared/interfaces/model';

import { ApolloQueryResult } from 'apollo-client';
import { Apollo } from 'apollo-angular';

@Injectable({
  providedIn: 'root'
})

export class PanelService {
  // [x: string]: any;

  readonly _panelNames = new BehaviorSubject<string[]>([]);
  readonly panelNames$ = this._panelNames.asObservable();

  readonly _selectedPanelRange = new BehaviorSubject<ISelectedPanelRange>(null);
  readonly selectedPanelRange$ = this._selectedPanelRange.asObservable();

  // for asset table
  readonly _assetTableData = new BehaviorSubject<IAssetTableData>(null);
  readonly assetTableData$ = this._assetTableData.asObservable();

  // for group table
  readonly _groupTableData = new BehaviorSubject<IAssetTableData>(null);
  readonly groupTableData$ = this._groupTableData.asObservable();

  readonly _selectedColumn = new BehaviorSubject<IAssetTableDelete>(null);
  readonly selectedColumn$ = this._selectedColumn.asObservable();

  readonly _assetTableFilter = new BehaviorSubject<string>('');
  readonly assetTableFilter$ = this._assetTableFilter.asObservable();

  readonly _refPanelTypes = new BehaviorSubject<IRefPanelTypes[]>([]);
  readonly refPanelTypes$ = this._refPanelTypes.asObservable();

  readonly _rowMetaData = new BehaviorSubject<any[]>([]);

  readonly _polarOptionChange = new BehaviorSubject<ISelectedPanelRange>(null);
  readonly polarOptionChange$ = this._polarOptionChange.asObservable();

  readonly _lineChartsGlobal = new BehaviorSubject<IGlobalLineCharts>(null);
  readonly lineChartsGlobal$ = this._lineChartsGlobal.asObservable();

  readonly _lineChartsHeaderRanges = new BehaviorSubject<IRangeForm>(null);
  readonly lineChartsHeaderRanges$ = this._lineChartsHeaderRanges.asObservable();

  readonly _features = new BehaviorSubject<IFeatures[]>([]);
  readonly features$ = this._features.asObservable();

// screens toggle on based of featuresDetails
  readonly _viewToggle = new BehaviorSubject<string>('');
  readonly viewToggle$ = this._viewToggle.asObservable();

  // for click on table cell to go that group/asset
  readonly _rowClickAssetGroupTable = new BehaviorSubject<IGroupAssetsTableRow>(null);
  readonly rowClickAssetGroupTable$ = this._rowClickAssetGroupTable.asObservable();

  // dashboard interval (for live-data interval)
  readonly _dashboardInterval = new BehaviorSubject<number>(null);
  readonly dashboardInterval$ = this._dashboardInterval.asObservable();

  // left side menu toggle state
  readonly _leftSideMenuState = new BehaviorSubject<boolean>(null);
  readonly leftSideMenuState$ = this._leftSideMenuState.asObservable();

  // check if TSCall exists against selected dashboard
  readonly _isTSCallExists = new BehaviorSubject<boolean>(false);
  readonly isTSCallExists$ = this._isTSCallExists.asObservable();

  // check if LiveDataCall exists against selected dashboard
  readonly _isLiveDataCallExists = new BehaviorSubject<boolean>(false);
  readonly isLiveDataCallExists$ = this._isLiveDataCallExists.asObservable();

  // on backToDashbaord page from alarms page
  readonly _isBackToDashbaord = new BehaviorSubject<boolean>(false);
  readonly isBackToDashbaord$ = this._isBackToDashbaord.asObservable();

  // alarms calls interval
  readonly _alarmsInterval = new BehaviorSubject<number | string>(null);
  readonly alarmsInterval$ = this._alarmsInterval.asObservable();

  // to handle header zoomout icon
  readonly _isZoomout = new BehaviorSubject<boolean>(false);
  readonly isZoomout$ = this._isZoomout.asObservable();

  readonly _selectedTabIndex = new BehaviorSubject<number>(EComponentTabs.DETAILS);
  readonly selectedTabIndex$ = this._selectedTabIndex.asObservable();

  readonly _panelConfigurations = new BehaviorSubject<any>(null);
  readonly panelConfigurations$ = this._panelConfigurations.asObservable();

  // onSavePanel click
  readonly _onSavePanel = new BehaviorSubject<boolean>(false);
  readonly onSavePanel$ = this._onSavePanel.asObservable();

  readonly _onEditChartDetails  = new BehaviorSubject<any>(null);
  readonly onEditChartDetails$ = this._onEditChartDetails.asObservable();

  readonly _savedPanelIds  = new BehaviorSubject<string[]>([]);

  // handle disable panel save button
  readonly _checkIsPanelDisabled  = new BehaviorSubject<boolean>(false);
  readonly checkIsPanelDisabled$ = this._checkIsPanelDisabled.asObservable();

  constructor(
    private readonly apollo: Apollo,
    private readonly router: Router,
    private readonly apiService: RestAPIService,
    private readonly toastr: ToastrService) { }

  get isModelPage() {
    return this.router.url.includes(EEntityTypes.MODELS);
  }

  get isComponentPage() {
    return this.router.url.includes(EEntityTypes.COMPONENTS);
  }

  get isPerspectivePage() {
    return this.router.url.includes(EEntityTypes.PERSPECTIVES);
  }

  /**
   * get main charts
   */
  getMainCharts() {
    return this.apiService.getGQLData({ query: getChartTypes.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) =>
        (response.data?.intellija_ref_chart_types || []).map(record => {
          return { name: record.name, value: record.id } || [];
        })
      ));
  }

  /**
 * get all panels
 * @param id entity id (model or component)
 */
  getPanelsById(ids: string[]) {
    console.log('ids: ', ids);
    return this.apiService.getGQLData({ query: getComponentPanelsByIds.query, params: { componentIds: ids } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) => (response.data?.intellija_panels || []).map(record => {
          return record || [];
        })
      ));
  }

 /**
 * get all panels
 * @param id perspective group id
 */
getGroupPanelsById(id: string) {
  console.log('id: ', id);
  return this.apiService.getGQLData({ query: getGroupPanelsById.query, params: { perspectiveGroupId: id } }).pipe(catchError(err => {
    const message = err.error.error?.message || 'An error has occured.';
    this.toastr.error(message);
    return EMPTY;
  }), map((response: any) =>
      (response.data?.intellija_panels || []).map(record => {
        return record || [];
      })
    ));
}

  /**
   * get sub charts by chart id
   * @param id chart id
   */
  getSubCharts(id: number) {
    return this.apiService.getGQLData({ query: this.isComponentPage ? getComponentSubChartTypes.query : this.isModelPage ? getModelSubChartTypes.query : getGroupSubChartTypes.query, params: { chartTypeId: id } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) =>
        (response.data?.intellija_ref_chart_sub_types || []).map(record => {
          return { id: record.id, title: record.name, thumbLink: record.thumb_link } || [];
        })
      ));
  }

  createPanel(payload: IPanelPayload): Observable<IPanelPayloadResponse> {
    return this.apiService.post<IPanelPayload>('panels', RestRequestType.CREATE_COMPONENT_PANEL, payload)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return err.error.error?.message;
      }), tap((res: any) => {
          if (res.result.status === 'ok') {
            this.toastr.success('Panel created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  updatePanel(payload: IPanelPayload, id: string): Observable<IPanelPayloadResponse> {
    return this.apiService.put<IPanelPayload>(`panels/${id}`, RestRequestType.UPDATE_COMPONENT_PANEL, payload)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return err.error.error?.message;
      }), tap((res: any) => {
          if (res.result.status === 'ok') {
            this.toastr.success('Panel updated successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  deletePanel(uuid: string) {
    return this.apiService.delete(`panels/${uuid}`)
      .toPromise().then(res => {
        this.toastr.success('Panel deleted successfully!');
        // this.loadAllModels();
        return Promise.resolve(true);
      }, err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return Promise.reject(err.error);
      });
  }

  /**
 * get panels against panel-ids
 * @param ids array of panel ids
 */
  getPanelsByPanelIds(ids: string[]) {
    console.log('ids: ', ids);
    return this.apiService.getGQLData({ query: getDashboardPanelsByPanelIds.query, params: { panelIds: ids } }).pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), map((response: any) =>
        (response.data?.intellija_panels || []).map(record => {
          return record || [];
        })));
  }

  /**
   * create dashboard
   * @param payload payload
   */
  createDashboard(payload: IDashboardPayload): Observable<any> {
    return this.apiService.post<IDashboardPayload>('dashboards', RestRequestType.CREATE_DASHBOARD, payload)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap((res: any) => {
          if (res.result.status === 'ok') {
            this.toastr.success('Dashboard created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

    /**
   * create dashboard
   * @param payload payload
   */
  updateDashboard(payload: IDashboardPayload, dashboardId: string): Observable<any> {
    return this.apiService.put<IDashboardPayload>(`dashboards/${dashboardId}`, RestRequestType.UPDATE_DASHBOARD, payload)
      .pipe(catchError(err => {
        const message = err.error?.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap((res: any) => {
          if (res.result.status === 'ok') {
            this.toastr.success('Dashboard updated successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  /**
   * delete dashboard
   * @param uuid dashboard id
   */
  deleteDashboard(uuid: string) {
    return this.apiService.delete(`dashboards/${uuid}`)
      .toPromise().then(res => {
        this.toastr.success('Dashbaords deleted successfully!');
        // this.loadAllModels();
        return Promise.resolve(true);
      }, err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return Promise.reject(err.error);
      });
  }

  /**
  * get all panels
  * @param id model id
  */
  getDashboardsById(id: string) {
    return this.apiService.getGQLData({ query: getDashboardsById.query, params: { modelId: id } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) =>
        (response.data?.intellija_dashboards || []).map(record => {
          return record || [];
        })
      ));
  }

    /**
  * get all panels
  * @param id perspective group id
  */
 getGroupDashboardsById(id: string) {
  return this.apiService.getGQLData({ query: getGroupDashboardsById.query, params: { perspectiveGroupId: id } }).pipe(catchError(err => {
    const message = err.error.error?.message || 'An error has occured.';
    this.toastr.error(message);
    return EMPTY;
  }), map((response: any) =>
      (response.data?.intellija_dashboards || []).map(record => {
        return record || [];
      })
    ));
}

  /**
   * get tags real time data (single Value)
   * @param payload payload
   */
  getTagsRealTimeSingularData(payload): Observable<any> {
    return this.apiService.post<any>('live-data', RestRequestType.TAGS_LIVE_DATA, payload)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap((res: any) => {
          if (res.result.status === 'ok') {
            // this.toastr.success('Dashboard created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

   /**
   * get live data against perspective's aggregation Tags
   * @param payload payload
   */
  getLiveDataForAggrTags(aggrPayload: IAggrPayload[]) {
    if(aggrPayload.length){
      const payload = { aggConfigs: aggrPayload };
      return this.apiService.post('agg-data', RestRequestType.GET_AGGREGATES, payload)
          .pipe(catchError(err => {
            const message = err.error?.error?.message || 'An error has occured.';
            this.toastr.error(message);
            return EMPTY;
          }), map(res => res.data?.configs));
    }

  }

  /**
   * get tags real time data (single Value)
   * @param payload payload
   */
  getTagsRealTimePluralData(payload): Observable<any> {
    return this.apiService.post<any>('timeseries', RestRequestType.TAGS_LIVE_DATA_SERIES, payload)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap((res: any) => {
          if (res.result.status === 'ok') {
            // this.toastr.success('Dashboard created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

    /**
   * get tags real time data (single Value)
   * @param payload payload
   */
  getTagsWriteBack(payload: IWriteBack): Observable<any> {
    return this.apiService.post<any>('writeback', RestRequestType.WRITE_BACK_REQ, payload)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), tap((res: any) => {
          if (res.result.status === 'ok') {
            // this.toastr.success('Dashboard created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }), map(res => res.data));
  }

  getRefPanelTypes(){
    return this.apiService.getGQLData({ query: getRefPanelTypes.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) =>
        (response?.data?.intellija_ref_panel_types || [])
      ));
  }

  getStartDate(range: ERanges) {
    const objRangeModel: IRangeForm = { range: null, fromDate: null, toDate: null };
    const currentDateTime: number = Date.now();
    objRangeModel.toDate = new Date(currentDateTime);
    switch (range) {
      case ERanges.H1: {
        objRangeModel.fromDate = new Date(currentDateTime - 3600000);
        return objRangeModel;
      }
      case ERanges.H6: {
        objRangeModel.fromDate = new Date(currentDateTime - 21600000);
        return objRangeModel;
      }
      case ERanges.H12: {
        objRangeModel.fromDate = new Date(currentDateTime - 43200000);
        return objRangeModel;
      }
      case ERanges.D1: {
        objRangeModel.fromDate = new Date(currentDateTime - 86400000);
        return objRangeModel;
      }
      case ERanges.W1: {
        objRangeModel.fromDate = new Date(currentDateTime - (86400000 * 7));
        return objRangeModel;
      }
      case ERanges.M1: {
        objRangeModel.fromDate = new Date(currentDateTime - (86400000 * 30));
        return objRangeModel;
      }
      case ERanges.M3: {
        objRangeModel.fromDate = new Date(currentDateTime - (86400000 * 90));
        return objRangeModel;
      }
      default: {
        console.log("!Invalid Range");
        break;
      }
    }
  }

  getLineChartAssetEvents(payload: IAssetEvents): Observable<IEventsResponse[]> {
    return this.apiService.post<IEventsResponse[]>('events', RestRequestType.ASSET_EVENTS, payload).pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
    }), map((res: any) => res.data?.events || [] as IEventsResponse[]));
  }

 /**
   * set  Ag Grid Row text style for Asset and Group Table
   */
  getRowStyle(chartData: any) {
    if (chartData?.panelSettings?.settings?.tagsTableColAlignment === ETagsTableColAlignments.Center) {
      return { 'text-align': 'center', };
    } else if (chartData?.panelSettings?.settings?.tagsTableColAlignment === ETagsTableColAlignments.Left) {
      return { 'text-align': 'left' };
    } else if (chartData?.panelSettings?.settings?.tagsTableColAlignment === ETagsTableColAlignments.Right) {
      return { 'text-align': 'right' };
    } else {
      return { 'text-align': 'center' };
    }
  };

  /**
   * set  Ag Grid Row Height for Asset and Group Table
   */
  getRowHeightCallback(chartData: any) {
    if (chartData?.panelSettings?.settings?.tagsTableCellHeight === ETagsTableCellHeights.Small) {
      return 40;
    } else if (chartData?.panelSettings?.settings?.tagsTableCellHeight === ETagsTableCellHeights.Medium) {
      return 60;
    } else if (chartData?.panelSettings?.settings?.tagsTableCellHeight === ETagsTableCellHeights.Large) {
      return 80;
    } else {
      return 50;
    }
  }
  /**
   * get all chart sub types for selected entity (component, model or group)
   */
  getAllChartTypes() {
    return this.apiService.getGQLData({ query: this.isComponentPage ? getChartsTypesForComponent.query : this.isModelPage ? getChartsTypesForModel.query : getChartsTypesForGroup.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      return (response.data?.intellija_ref_chart_sub_types || []) as IDropdown[];
    }));
  }
}